/**
 * 시각 칸 — 적어도 되고, 시·분 목록에서 골라도 된다(2026-10-21, 운영 시간부터).
 *
 * `<input type="time">` 은 사파리·웹킷에 고를 목록이 없어 숫자를 칸마다 쳐 넣어야 했고, 한국어 로캘에서는
 * "오후 06:30" 으로 그려졌다. 이 칸은 늘 24시간 `HH:MM` 이다.
 *
 * - 칸이나 시계 단추를 누르면 아래에 시(00–23)·분(5분 간격) 목록이 열린다. 시를 고르면 열린 채 분을 고르고,
 *   분을 고르면 닫힌다.
 * - 적어도 된다 — "730"·"19:30"·"7시 30분" 을 칸을 떠날 때·Enter 에 읽는다(`parseTimeText`). 못 읽으면 앞의
 *   값으로 돌아간다. ↑·↓ 는 5분씩.
 * - 목록은 문서 끝에 띄운다(⋯ 메뉴·필터와 같다) — 패널의 `overflow: hidden` 에 잘리지 않게.
 * - 초점은 칸에 남는다. 목록 단추는 Tab 으로 가지 않고, 창(Modal) 안에서 Esc 는 목록만 닫는다.
 */
import { Clock } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { placeMenu } from './MoreMenu.js';
import { HOUR_CHOICES, minuteChoices, parseTimeText, stepTime } from './timeText.js';

export function TimeField({
  value,
  onChange,
  label,
  disabled = false,
}: {
  /** `HH:MM`(24시간). */
  value: string;
  onChange: (next: string) => void;
  /** 화면 낭독기와 목록 머리에 쓰는 이름 — "시작 시각". */
  label: string;
  disabled?: boolean;
}): ReactNode {
  const id = useId();
  const wrap = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  /** 적는 중인 글자 — 칸을 떠나거나 Enter 를 누를 때 읽는다. 적지 않는 동안은 `value` 를 그대로 보인다. */
  const [draft, setDraft] = useState<string | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setPosition(null);
  }, []);

  const commit = (): void => {
    if (draft === null) return;
    const parsed = parseTimeText(draft);
    setDraft(null);
    if (parsed && parsed !== value) onChange(parsed);
  };

  useLayoutEffect(() => {
    if (!open || !wrap.current || !box.current) return;
    const a = wrap.current.getBoundingClientRect();
    const b = box.current.getBoundingClientRect();
    // 칸의 왼쪽 끝에 맞춘다 — placeMenu 는 오른쪽 끝에 맞추므로 목록 폭만큼 넘겨 준다.
    const next = placeMenu(
      { top: a.top, bottom: a.bottom, left: a.left, right: a.left + b.width },
      { width: b.width, height: b.height },
      { width: window.innerWidth, height: window.innerHeight },
    );
    setPosition((current) => (current && current.top === next.top && current.left === next.left ? current : next));
  });

  // 열 때 지금 값이 목록 가운데 보이게.
  useEffect(() => {
    if (!open || !position) return;
    for (const selected of box.current?.querySelectorAll<HTMLElement>('[aria-selected="true"]') ?? []) {
      const list = selected.parentElement;
      if (list) list.scrollTop = selected.offsetTop - list.clientHeight / 2 + selected.offsetHeight / 2;
    }
  }, [open, position === null]); // eslint-disable-line react-hooks/exhaustive-deps -- 처음 자리 잡았을 때 한 번.

  useEffect(() => {
    if (!open) return;
    const inside = (target: EventTarget | null): boolean =>
      target instanceof Node && (wrap.current?.contains(target) === true || box.current?.contains(target) === true);
    const onPointer = (event: PointerEvent): void => {
      if (!inside(event.target)) close();
    };
    // 목록 안의 스크롤은 닫을 일이 아니다.
    const onScroll = (event: Event): void => {
      if (!inside(event.target)) close();
    };
    document.addEventListener('pointerdown', onPointer, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  const [hour = '00', minute = '00'] = /^\d{2}:\d{2}$/.test(value) ? value.split(':') : [];
  const pick = (next: string, done: boolean): void => {
    setDraft(null);
    if (next !== value) onChange(next);
    if (done) close();
  };

  return (
    <div ref={wrap} className="time-field">
      <input
        ref={input}
        className="input input-time"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="00:00"
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-haspopup="listbox"
        disabled={disabled}
        value={draft ?? value}
        onChange={(event) => setDraft(event.target.value)}
        onClick={() => {
          if (!disabled) setOpen(true);
        }}
        onBlur={() => {
          // 목록·시계 단추를 누를 때는 초점이 떠나지 않는다(누름을 막아 둔다) — 여기 오는 것은 정말 떠난 것이다.
          commit();
          close();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
            close();
          } else if (event.key === 'Escape' && open) {
            // 목록만 닫는다 — 창(Modal)이 이 키로 닫히지 않게(`defaultPrevented` 를 본다).
            event.preventDefault();
            setDraft(null);
            close();
          } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            const base = (draft !== null ? parseTimeText(draft) : null) ?? value;
            pick(stepTime(base, event.key === 'ArrowUp' ? 5 : -5), false);
          }
        }}
      />
      <button
        type="button"
        className="time-field__toggle"
        tabIndex={-1}
        disabled={disabled}
        aria-label={`${label} 고르기`}
        onPointerDown={(event) => event.preventDefault()}
        onClick={() => {
          if (open) close();
          else {
            setOpen(true);
            input.current?.focus();
          }
        }}
      >
        <Clock className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={box}
              id={id}
              className="time-pop"
              style={position ?? { top: -9999, left: -9999 }}
              // 초점은 칸에 남긴다 — 목록을 눌러도 칸이 blur 되어 적던 글자를 먼저 읽지 않게.
              onPointerDown={(event) => event.preventDefault()}
            >
              <div className="time-pop__col">
                <span className="time-pop__head">시</span>
                <div className="time-pop__list" role="listbox" aria-label={`${label} — 시`}>
                  {HOUR_CHOICES.map((choice) => (
                    <button
                      key={choice}
                      type="button"
                      tabIndex={-1}
                      role="option"
                      aria-selected={choice === hour}
                      className="time-pop__item"
                      onClick={() => pick(`${choice}:${minute}`, false)}
                    >
                      {choice}
                    </button>
                  ))}
                </div>
              </div>
              <div className="time-pop__col">
                <span className="time-pop__head">분</span>
                <div className="time-pop__list" role="listbox" aria-label={`${label} — 분`}>
                  {minuteChoices(value).map((choice) => (
                    <button
                      key={choice}
                      type="button"
                      tabIndex={-1}
                      role="option"
                      aria-selected={choice === minute}
                      className="time-pop__item"
                      onClick={() => pick(`${hour}:${choice}`, true)}
                    >
                      {choice}
                    </button>
                  ))}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
